import { authAdapter } from '@/app/_auth/auth'
import { getKeycloak } from '@/app/_auth/keycloak-auth'
import type { RegistrationForm } from '@/app/_auth/steps/_common/registration-form-context'
import { usersApiPaths } from '@/hooks/api/_paths'
import type { ConsentSettingsPolicyData } from '@/hooks/api/_types'
import { useApiRequest } from '@/lib/http-utils'
import { isVPCMode } from '@/lib/utils'
import { useMutation, type UseMutationOptions } from '@tanstack/react-query'

type RegistrationAPIRequestBody = {
  name: string
  email: string
  organization: string
  role: string
  org_size: string
  industry: string
  purpose: string
} & ConsentSettingsPolicyData

export function useRegistrationMutation(
  options?: UseMutationOptions<void, unknown, RegistrationForm>,
) {
  const { apiRequest } = useApiRequest()
  return useMutation({
    retry: 3,
    mutationKey: ['register-user'],
    mutationFn: async (data: RegistrationForm) => {
      const response = await apiRequest({
        path: usersApiPaths.register,
        options: {
          method: 'POST',
          body: JSON.stringify(port(data)),
        },
      })

      if (response.status === 400) {
        throw new Error('INVALID_REGISTRATION_REQUEST_BODY')
      }

      if (response.status !== 200) {
        throw new Error('FAILED_TO_REGISTER_USER')
      }
    },
    ...options,
  })
}

function port(data: RegistrationForm): RegistrationAPIRequestBody {
  let email: string | null = null

  if (isVPCMode) {
    const keycloak = getKeycloak()
    email = keycloak?.tokenParsed?.email || keycloak?.profile?.email || null
  } else {
    email = authAdapter?.getCurrentUser()?.email ?? null
  }

  if (!email) throw new Error('USER_EMAIL_NOT_FOUND')

  return {
    email,
    name: data.fullName?.trim(),
    organization: data.orgName?.trim(),
    role: data.role?.trim(),
    org_size: data.orgSize.trim(),
    industry: data.otherIndustry?.trim() || data.industry.trim(),
    purpose: data.purpose?.trim(),
    policies: [
      { name: 'ALLOW_DATA', preference: data.allowData },
      { name: 'ALLOW_MARKETING_COMMS', preference: data.allowMarketing },
    ],
  }
}
